import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    MagnifyingGlassIcon,
    UserCircleIcon,
    DocumentArrowDownIcon,
    XMarkIcon,
    SparklesIcon,
    AcademicCapIcon,
    BriefcaseIcon
} from '@heroicons/react/24/outline';
import CandidateJobsPanel from '../../components/CandidateJobsPanel';
import { FEATURES } from '../../config/features';

const TalentPool = () => {
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        location: '',
        domain: ''
    });
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [notes, setNotes] = useState('');

    const fetchCandidates = async () => {
        setLoading(true);
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            const userStr = localStorage.getItem('user');
            const user = userStr ? JSON.parse(userStr) : null;
            const token = user?.token;

            const params = {
                page,
                limit: 10,
                ...filters
            };

            const res = await axios.get(`${apiUrl}/api/talent`, {
                params,
                headers: { Authorization: `Bearer ${token}` }
            });

            setCandidates(res.data.candidates);
            setTotalPages(res.data.totalPages);
        } catch (err) {
            console.error('Error fetching talent pool:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCandidates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filters.status]); // Debounce search in real app, specific effect for filters

    const handleFilterChange = (e) => {
        setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
        setPage(1); // Reset to page 1 on filter change
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        fetchCandidates();
    };

    const openCandidate = (candidate) => {
        setSelectedCandidate(candidate);
        setNotes(candidate.recruiterNotes || '');
    };

    const updateStatus = async (newStatus) => {
        if (!selectedCandidate) return;
        setStatusUpdating(true);
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            const userStr = localStorage.getItem('user');
            const user = userStr ? JSON.parse(userStr) : null;
            const token = user?.token;

            await axios.post(`${apiUrl}/api/talent/${selectedCandidate._id}/status`, {
                status: newStatus,
                notes: notes
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Update local state
            setSelectedCandidate(prev => ({ ...prev, recruiterStatus: newStatus, recruiterNotes: notes }));

            // Update entry in list
            setCandidates(prev => prev.map(c =>
                c._id === selectedCandidate._id ? { ...c, recruiterStatus: newStatus, recruiterNotes: notes } : c
            ));

        } catch (err) {
            console.error('Error updating status:', err);
            alert('Failed to update status');
        } finally {
            setStatusUpdating(false);
        }
    };

    return (
        <div className="bg-white min-h-screen">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                <h1 className="text-2xl font-bold text-gray-900">Talent Pool</h1>
                <p className="mt-1 text-sm text-gray-500">Manage incoming candidate applications.</p>
            </div>

            {/* Filters */}
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row gap-4 items-center justify-between">
                <form onSubmit={handleSearchSubmit} className="relative flex-1 w-full max-w-lg">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>
                    <input
                        type="text"
                        name="search"
                        value={filters.search}
                        onChange={handleFilterChange}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Search candidates by name, email, or role..."
                    />
                </form>

                <div className="flex gap-2 w-full sm:w-auto flex-wrap">
                    <select
                        name="status"
                        value={filters.status}
                        onChange={handleFilterChange}
                        className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    >
                        <option value="">All Statuses</option>
                        <option value="new">New</option>
                        <option value="shortlisted">Shortlisted</option>
                        <option value="contacted">Contacted</option>
                        <option value="rejected">Rejected</option>
                    </select>
                    <select
                        name="domain"
                        value={filters.domain}
                        onChange={handleFilterChange}
                        className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    >
                        <option value="">All Domains</option>
                        <option value="Software Engineering">Software Engineering</option>
                        <option value="Data Science">Data Science & Analytics</option>
                        <option value="Biology">Biology & Life Sciences</option>
                        <option value="Healthcare">Healthcare & Medical</option>
                        <option value="Finance">Finance & Accounting</option>
                        <option value="Marketing">Marketing & Communications</option>
                        <option value="Sales">Sales & Business Development</option>
                        <option value="Human Resources">Human Resources</option>
                        <option value="Design">Design & Creative</option>
                    </select>
                    <button
                        onClick={fetchCandidates}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Candidate</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain & Skills</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role & Location</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applied</th>
                            <th scope="col" className="relative px-6 py-3">
                                <span className="sr-only">View</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                            <tr><td colSpan="6" className="px-6 py-10 text-center text-sm text-gray-500">Loading...</td></tr>
                        ) : candidates.length === 0 ? (
                            <tr><td colSpan="6" className="px-6 py-10 text-center text-sm text-gray-500">No candidates found.</td></tr>
                        ) : (
                            candidates.map((candidate) => (
                                <tr key={candidate._id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openCandidate(candidate)}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10 relative">
                                                <UserCircleIcon className="h-10 w-10 text-gray-300" />
                                                {candidate.isAutotagged && (
                                                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-purple-500" title="AI Enhanced">
                                                        <SparklesIcon className="h-3 w-3 text-white" />
                                                    </span>
                                                )}
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">{candidate.fullName}</div>
                                                <div className="text-sm text-gray-500">{candidate.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm font-medium text-gray-900">
                                            {candidate.domain || 'General'}
                                        </div>
                                        <div className="flex flex-wrap gap-1 mt-1 max-w-xs">
                                            {candidate.skills?.slice(0, 3).map((skill, idx) => (
                                                <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                                    {skill}
                                                </span>
                                            ))}
                                            {candidate.skills?.length > 3 && (
                                                <span className="text-xs text-gray-500">+{candidate.skills.length - 3} more</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{candidate.desiredRoles || 'N/A'}</div>
                                        <div className="text-sm text-gray-500">{candidate.location || 'Remote'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                      ${candidate.recruiterStatus === 'new' ? 'bg-blue-100 text-blue-800' :
                                                candidate.recruiterStatus === 'shortlisted' ? 'bg-green-100 text-green-800' :
                                                    candidate.recruiterStatus === 'rejected' ? 'bg-red-100 text-red-800' :
                                                        'bg-gray-100 text-gray-800'}`}>
                                            {candidate.recruiterStatus?.charAt(0).toUpperCase() + candidate.recruiterStatus?.slice(1)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(candidate.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button className="text-indigo-600 hover:text-indigo-900">View</button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
                {/* Desktop Pagination could go here */}
            </div>

            {/* Slide-over / Modal for Details */}
            {selectedCandidate && (
                <div className="fixed inset-0 overflow-hidden z-50">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedCandidate(null)}></div>
                        <section className="absolute inset-y-0 right-0 pl-10 max-w-full flex">
                            <div className="w-screen max-w-md">
                                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll">
                                    <div className="px-4 py-6 sm:px-6 bg-indigo-700">
                                        <div className="flex items-start justify-between">
                                            <h2 className="text-lg font-medium text-white"> Candidate Details </h2>
                                            <div className="ml-3 h-7 flex items-center">
                                                <button onClick={() => setSelectedCandidate(null)} className="bg-indigo-700 rounded-md text-indigo-200 hover:text-white focus:outline-none">
                                                    <XMarkIcon className="h-6 w-6" />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <h3 className="text-2xl font-bold text-white">{selectedCandidate.fullName}</h3>
                                            <p className="text-indigo-200">{selectedCandidate.email}</p>
                                        </div>
                                    </div>

                                    <div className="p-6 space-y-6 flex-1">

                                        {/* AI Badge */}
                                        {selectedCandidate.isAutotagged && (
                                            <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg">
                                                <SparklesIcon className="h-5 w-5 text-purple-600" />
                                                <span className="text-sm font-medium text-purple-700">AI Enhanced Profile</span>
                                            </div>
                                        )}

                                        {/* AI Summary */}
                                        {selectedCandidate.summary && (
                                            <div>
                                                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">AI Summary</h4>
                                                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">{selectedCandidate.summary}</p>
                                            </div>
                                        )}

                                        {/* Domain & Match Score */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <h4 className="text-xs text-gray-500 uppercase flex items-center gap-1">
                                                    <BriefcaseIcon className="h-3 w-3" /> Domain
                                                </h4>
                                                <p className="text-sm font-medium text-gray-900">{selectedCandidate.domain || 'General'}</p>
                                            </div>
                                            <div>
                                                <h4 className="text-xs text-gray-500 uppercase">Match Score</h4>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                                                        <div
                                                            className={`h-2 rounded-full ${selectedCandidate.matchScore >= 70 ? 'bg-green-500' :
                                                                selectedCandidate.matchScore >= 40 ? 'bg-yellow-500' : 'bg-gray-400'
                                                                }`}
                                                            style={{ width: `${selectedCandidate.matchScore || 0}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-sm font-medium text-gray-900">{selectedCandidate.matchScore || 0}%</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Skills */}
                                        {selectedCandidate.skills?.length > 0 && (
                                            <div>
                                                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Skills</h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {selectedCandidate.skills.map((skill, idx) => (
                                                        <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                                            {skill}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Education */}
                                        {selectedCandidate.education && (
                                            <div>
                                                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                                                    <AcademicCapIcon className="h-4 w-4" /> Education
                                                </h4>
                                                <p className="text-sm text-gray-700">{selectedCandidate.education}</p>
                                            </div>
                                        )}

                                        {/* Status Actions */}
                                        <div>
                                            <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Update Status</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {['new', 'shortlisted', 'contacted', 'rejected'].map(status => (
                                                    <button
                                                        key={status}
                                                        onClick={() => updateStatus(status)}
                                                        disabled={statusUpdating}
                                                        className={`px-3 py-1 rounded-full text-sm font-medium border ${selectedCandidate.recruiterStatus === status
                                                            ? 'bg-indigo-600 text-white border-indigo-600'
                                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        {status.charAt(0).toUpperCase() + status.slice(1)}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Job Assignments Section — multi-job mapping (backend pending Supabase reimpl) */}
                                        {FEATURES.multiJob && (
                                            <div className="border-t border-gray-200 pt-6">
                                                <CandidateJobsPanel
                                                    candidateId={selectedCandidate._id}
                                                    candidateName={selectedCandidate.fullName}
                                                />
                                            </div>
                                        )}

                                        <div>
                                            <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Resume</h4>
                                            <a
                                                href={`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${selectedCandidate.resumeFileUrl}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                            >
                                                <DocumentArrowDownIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
                                                Download Resume
                                            </a>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <h4 className="text-xs text-gray-500 uppercase">Phone</h4>
                                                <p className="text-sm font-medium text-gray-900">{selectedCandidate.phone || '-'}</p>
                                            </div>
                                            <div>
                                                <h4 className="text-xs text-gray-500 uppercase">Location</h4>
                                                <p className="text-sm font-medium text-gray-900">{selectedCandidate.location || '-'}</p>
                                            </div>
                                            <div className="col-span-2">
                                                <h4 className="text-xs text-gray-500 uppercase">Desired Roles</h4>
                                                <p className="text-sm font-medium text-gray-900">{selectedCandidate.desiredRoles || '-'}</p>
                                            </div>
                                            <div className="col-span-2">
                                                <h4 className="text-xs text-gray-500 uppercase">Visa Status</h4>
                                                <p className="text-sm font-medium text-gray-900">{selectedCandidate.visaStatus || '-'}</p>
                                            </div>
                                            <div className="col-span-2">
                                                <h4 className="text-xs text-gray-500 uppercase">LinkedIn</h4>
                                                {selectedCandidate.linkedinUrl ? (
                                                    <a href={selectedCandidate.linkedinUrl} target="_blank" rel="noreferrer" className="text-sm text-indigo-600 hover:text-indigo-500 break-all">{selectedCandidate.linkedinUrl}</a>
                                                ) : <p className="text-sm text-gray-900">-</p>}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Internal Notes</label>
                                            <div className="mt-1">
                                                <textarea
                                                    rows={4}
                                                    value={notes}
                                                    onChange={(e) => setNotes(e.target.value)}
                                                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                                                    placeholder="Add notes about this candidate..."
                                                />
                                            </div>
                                            <div className="mt-2 flex justify-end">
                                                <button
                                                    onClick={() => updateStatus(selectedCandidate.recruiterStatus)} // Save notes primarily
                                                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                >
                                                    Save Notes
                                                </button>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TalentPool;
